import React from 'react';
import { MatrixFigure } from './figures';

// Reached only when the block carries no response_window_ms at all. The served
// value is 20000ms and this matches it, so the fallback is never a silent
// change of administration.
const DEFAULT_WINDOW_MS = 20000;

// This instrument serves no inter-stimulus interval. The fixation and the gap
// are fixed administration constants that sit ON TOP of the served response
// window. flanker and stroop fit their fixation INSIDE a served isi_ms; that
// convention belongs to those instruments and is deliberately not imported here.
const FIXATION_MS = 400;
const GAP_MS = 500;

const CELL_SIZE = 72;
const OPTION_SIZE = 62;

// matrix_size is 3, so a well-formed item is 3x3. The column count is read off
// the served matrix rather than pinned to a constant, so a matrix of another
// width lays out as a matrix instead of reflowing into a ragged strip.
const DEFAULT_COLUMNS = 3;

// matrix[2][2] is null on purpose and is drawn as an empty slot at the bottom
// right — that is the cell the participant completes. Nothing here inspects the
// options, induces the rule, or fills the hole: the missing figure is exactly
// what the item withholds.
function StimulusView({ stimulus }) {
  const matrix = Array.isArray(stimulus?.matrix) ? stimulus.matrix : [];
  const columns = (Array.isArray(matrix[0]) && matrix[0].length) || DEFAULT_COLUMNS;
  return (
    <div
      role="group"
      aria-label="Pattern"
      style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, auto)`, gap: 'var(--space-2)' }}
    >
      {matrix.flatMap((row, r) =>
        (Array.isArray(row) ? row : []).map((figure, c) => (
          <div
            key={`${r}-${c}`}
            style={{
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--glass-bg-light)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <MatrixFigure
              figure={figure}
              size={CELL_SIZE}
              label={figure ? undefined : 'missing cell'}
            />
          </div>
        ))
      )}
    </div>
  );
}

export const matrixReasoningTask = {
  id: 'matrix-reasoning',
  name: 'Patterns',
  pillar: 'problem',

  // A power test, and the reason this flag exists. The window is 20000ms and is
  // a safety valve, not a manipulation: accuracy scores the item and reaction
  // time does not. Four options, one of which must be chosen, so no trial can
  // end any way but in a response — nothing to leak. Holding the full window
  // here meant a participant who solved an item in six seconds sat in front of
  // a dead screen for fourteen more, which reads as a hang, not as an
  // instrument.
  holdsFullWindow: false,

  instructions: [
    'Eight figures form a pattern, with the bottom-right one missing.',
    'Work out the rule and pick the figure that completes it.',
    'Take your time — this one is not about speed.',
  ],

  // Options come from the stimulus, so there is no static target set. The
  // fallback is empty rather than a placeholder: a wrong-shaped target array
  // would be submitted as a real choice.
  targets: [],

  // Deliberately empty. A worked example has to name a correct response, and
  // which option completes the pattern is exactly what the payload withholds —
  // writing one by hand would publish an answer key the server owns.
  examples: [],

  // The index is into `options` AS SERVED. Every attribute is split two-two
  // across the four options, so plurality voting and odd-one-out both pay
  // exactly chance — a property that depends on the served set arriving intact.
  toAnswer: (targetId) => ({ choice: Number(targetId) }),

  // One target per served option, in served order. Nothing is sorted, deduped,
  // filtered or reordered: the served position IS the graded answer, and the
  // two-two split of every attribute across the four slots is what makes the
  // item honest.
  targetsFor: (trial) => {
    const options = trial?.stimulus?.options;
    if (!Array.isArray(options)) return [];
    return options.map((figure, i) => ({
      id: String(i),
      label: `Option ${i + 1}`,
      keys: [String(i + 1)],
      hint: String(i + 1),
      node: <MatrixFigure figure={figure} size={OPTION_SIZE} label={`Option ${i + 1}`} />,
    }));
  },

  // A POWER test. response_window_ms is 20000 and is a SAFETY VALVE, not a
  // manipulation: accuracy drives the score and reaction time does not. There is
  // deliberately no clock anywhere in this module — showing one makes the item a
  // speed test and changes what is measured. A 15-second solve is a good answer,
  // not a slow one.
  //
  // Number() is load-bearing: the runner bounds reaction time at windowMs + 250,
  // so a window arriving as the string "20000" would concatenate to "20000250"
  // and accept every late response. `||` rather than `??` so a served 0 falls
  // back instead of timing every trial out instantly.
  timing: (params) => ({
    fixationMs: FIXATION_MS,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: GAP_MS,
  }),

  // The correct option is not in the payload, and inducing the rule client-side
  // would be reconstructing a withheld answer — so there is nothing to feed
  // back, on a practice trial or anywhere else.
  feedbackAvailable: false,
  practiceExpectation: () => null,

  StimulusView,
};
